import { App } from "antd"
import { maxBy } from "lodash"
import { useEffect, useMemo, useRef } from "react"

import { versionAtoms } from "#atoms/allAtoms"
import CountdownDisplay from "#blocks/CountdownDisplay"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { useAllDocs } from "#hooks/jotai/useSubscription"

const RELOAD_DELAY_SECONDS = 5

const VersionChecker = () => {
  const { notification } = App.useApp()
  const versionDocs = useAllDocs(versionAtoms, DOC_TYPES.VERSIONS)
  const latestVersionDoc = useMemo(
    () => maxBy(versionDocs, (doc) => doc.version),
    [versionDocs]
  )

  const lastNotifiedVersion = useRef<string | null>(null)
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )

  useEffect(() => {
    if (!latestVersionDoc) {
      return
    }

    const serverVersion = latestVersionDoc.version

    if (
      serverVersion !== lastNotifiedVersion.current &&
      lastNotifiedVersion.current !== null
    ) {
      console.log(
        `🔄 Version changed: ${lastNotifiedVersion.current} → ${serverVersion}`
      )
      lastNotifiedVersion.current = serverVersion

      let countdown = RELOAD_DELAY_SECONDS
      const notificationKey = `version-update-${Date.now()}`

      const updateNotification = () => {
        notification.info({
          key: notificationKey,
          message: "A new version is available.",
          description: (
            <CountdownDisplay
              label="The page will automatically refresh in"
              seconds={countdown}
            />
          ),
          duration: 0,
          placement: "topRight",
        })

        if (countdown > 0) {
          countdown--
        } else {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current)
            countdownIntervalRef.current = null
          }
          window.location.reload()
        }
      }

      updateNotification()
      countdownIntervalRef.current = setInterval(updateNotification, 1000)
    } else if (lastNotifiedVersion.current === null) {
      console.log(`📌 Initial version loaded: ${serverVersion}`)
      lastNotifiedVersion.current = serverVersion
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [latestVersionDoc, notification])

  return null
}

export default VersionChecker
